import { useState, useEffect } from 'react';
import { useTTSStore } from '../../store/tts';
import { useUserStore } from '../../store/user';
import toast from 'react-hot-toast';

function TextInput({ defaultVoiceCleanerEnabled = false }) {
  const [text, setText] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [voiceCleanerEnabled, setVoiceCleanerEnabled] = useState(defaultVoiceCleanerEnabled);
  const [speed, setSpeed] = useState(1.0);
  const [pendingSpeed, setPendingSpeed] = useState(1.0);
  const { 
    convert, 
    isConverting, 
    selectedVoice, 
    currentAudio, 
    applySpeedToAudio,
    languages,
    selectedLanguage,
    setLanguage,
    fetchLanguages,
    isLoadingLanguages,
    textModels,
    selectedTextModel,
    setTextModel,
    fetchTextModels,
    isLoadingTextModels,
    generateText,
    isGeneratingText,
  } = useTTSStore();
  const { usageStats } = useUserStore();
  const availableLanguages = Array.isArray(languages) ? languages : [];
  const availableTextModels = Array.isArray(textModels) ? textModels : [];

  // Fetch languages on mount
  useEffect(() => {
    if (availableLanguages.length === 0 && fetchLanguages) {
      fetchLanguages();
    }
    if (availableTextModels.length === 0 && fetchTextModels) {
      fetchTextModels();
    }
  }, [availableLanguages.length, availableTextModels.length, fetchLanguages, fetchTextModels]);

  useEffect(() => {
    setVoiceCleanerEnabled(defaultVoiceCleanerEnabled);
  }, [defaultVoiceCleanerEnabled]);

  const maxCharacters = usageStats?.max_characters || 500;
  const characterCount = text.length;
  const remainingCharacters = maxCharacters - characterCount;
  const isClonedVoiceSelected = Boolean(
    selectedVoice?.is_cloned || selectedVoice?.voice_id?.startsWith('clone_')
  );

  // Check if speed has changed from the current audio's speed
  const hasSpeedChanged = currentAudio && pendingSpeed !== speed;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!text.trim()) {
      toast.error('Please enter some text');
      return;
    }

    if (!selectedVoice) {
      toast.error('Please select a voice');
      return;
    }

    setSpeed(pendingSpeed);
    const result = await convert(text, {
      speed: pendingSpeed,
      voiceCleaner: voiceCleanerEnabled,
    });
    
    if (result.success) {
      toast.success('Audio generated successfully!');
    } else {
      toast.error(result.error);
    }
  };

  const handleGenerateText = async (speakAfterGenerate = false) => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a content prompt');
      return;
    }

    const generationResult = await generateText(aiPrompt, {
      model: selectedTextModel || undefined,
    });

    if (!generationResult.success) {
      toast.error(generationResult.error);
      return;
    }

    const generatedText = generationResult.data?.text?.trim();
    if (!generatedText) {
      toast.error('Generated content is empty');
      return;
    }

    setText(generatedText);
    toast.success('Content generated');

    if (!speakAfterGenerate) {
      return;
    }

    if (!selectedVoice) {
      toast.error('Please select a voice');
      return;
    }

    setSpeed(pendingSpeed);
    const convertResult = await convert(generatedText, {
      speed: pendingSpeed,
      voiceCleaner: voiceCleanerEnabled,
    });
    if (convertResult.success) {
      toast.success('Content generated and converted to speech');
    } else {
      toast.error(convertResult.error);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    setPendingSpeed(newSpeed);
    // If we have current audio, apply speed change immediately for preview
    if (currentAudio && applySpeedToAudio) {
      applySpeedToAudio(newSpeed);
    }
  };

  const handleApplySettings = () => {
    if (!currentAudio) return;
    setSpeed(pendingSpeed);
    toast.success(`Speed set to ${pendingSpeed.toFixed(2)}x`);
  };

  const handleRegenerate = async () => {
    if (!text.trim() || !selectedVoice) return;
    
    setSpeed(pendingSpeed);
    const result = await convert(text, {
      speed: pendingSpeed,
      voiceCleaner: voiceCleanerEnabled,
    });
    
    if (result.success) {
      toast.success('Audio regenerated with new settings!');
    } else {
      toast.error(result.error);
    }
  };

  const speedLabels = {
    0.5: 'Very Slow',
    0.75: 'Slow', 
    1.0: 'Normal',
    1.25: 'Fast',
    1.5: 'Very Fast',
    2.0: 'Maximum'
  };

  const getSpeedLabel = (val) => {
    const closest = Object.keys(speedLabels).reduce((a, b) => 
      Math.abs(parseFloat(b) - val) < Math.abs(parseFloat(a) - val) ? b : a
    );
    return speedLabels[closest];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="ai-prompt-input" className="text-sm font-medium">
              ✍️ AI Content Generator
            </label>
            <span className="text-xs text-gray-400">Generate text then speak instantly</span>
          </div>

          <textarea
            id="ai-prompt-input"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="Describe the content you want (e.g., 30-second product intro in friendly tone)..."
            className="textarea h-24 mb-3"
            disabled={isGeneratingText || isConverting}
          />

          <div className="grid md:grid-cols-3 gap-2">
            <select
              value={selectedTextModel || ''}
              onChange={(e) => setTextModel(e.target.value)}
              className="input md:col-span-1"
              disabled={isLoadingTextModels || isGeneratingText || isConverting || availableTextModels.length === 0}
            >
              {availableTextModels.length > 0 ? (
                availableTextModels.map((item) => (
                  <option key={`${item.provider_id}:${item.model}`} value={item.model}>
                    {item.model}
                  </option>
                ))
              ) : (
                <option value="">No models configured</option>
              )}
            </select>

            <button
              type="button"
              className="btn btn-secondary md:col-span-1"
              onClick={() => handleGenerateText(false)}
              disabled={!aiPrompt.trim() || isGeneratingText || isConverting || availableTextModels.length === 0}
            >
              {isGeneratingText ? 'Generating...' : 'Generate Text'}
            </button>

            <button
              type="button"
              className="btn btn-primary md:col-span-1"
              onClick={() => handleGenerateText(true)}
              disabled={!aiPrompt.trim() || isGeneratingText || isConverting || !selectedVoice || availableTextModels.length === 0}
            >
              {isGeneratingText ? 'Generating...' : 'Generate & Speak'}
            </button>
          </div>
        </div>

        <label htmlFor="text-input" className="block text-sm font-medium mb-2">
          Enter your text
        </label>
        <textarea
          id="text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type or paste your text here..."
          className="textarea h-40"
          maxLength={maxCharacters}
          disabled={isConverting}
        />
        
        <div className="flex justify-between mt-2 text-sm">
          <span className={`${remainingCharacters < 50 ? 'text-red-400' : 'text-gray-400'}`}>
            {characterCount} / {maxCharacters} characters
          </span>
          {remainingCharacters < 100 && (
            <span className="text-yellow-400">
              {remainingCharacters} remaining
            </span>
          )}
        </div>
      </div>

      {/* Language Selector - only shown for multilingual voices (XTTS, YourTTS) */}
      {selectedVoice?.is_multilingual && !isClonedVoiceSelected && (
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="language-select" className="text-sm font-medium">
              🌍 Output Language
            </label>
            <span className="text-xs text-gray-400">
              Multilingual voice - speak in any language
            </span>
          </div>
          <select
            id="language-select"
            value={selectedLanguage || 'en'}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-purple-500 focus:outline-none"
            disabled={isConverting || isLoadingLanguages}
          >
            {availableLanguages.length > 0 ? (
              availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </option>
              ))
            ) : (
              <option value="en">English (en)</option>
            )}
          </select>
        </div>
      )}

      {/* Speed Control */}
      <div className="bg-gray-800/50 rounded-lg p-4">
        <div className="mb-4 p-3 rounded-lg border border-purple-500/30 bg-purple-500/5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={voiceCleanerEnabled}
              onChange={(e) => setVoiceCleanerEnabled(e.target.checked)}
              className="mt-1"
              disabled={isConverting}
            />
            <div>
              <p className="text-sm font-medium">✨ Voice Cleaner</p>
              <p className="text-xs text-gray-400">
                Removes hum/static, reduces echo and mouth sounds, smooths filler/stutter artifacts,
                preserves background music, auto-EQs, and normalizes for polished output.
              </p>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium">
            Speech Speed
          </label>
          <span className="text-sm text-purple-400 font-medium">
            {pendingSpeed.toFixed(2)}x - {getSpeedLabel(pendingSpeed)}
          </span>
        </div>
        <input
          type="range"
          min={0.5}
          max={2.0}
          step={0.05}
          value={pendingSpeed}
          onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          disabled={isConverting}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0.5x</span>
          <span>1.0x</span>
          <span>2.0x</span>
        </div>
        
        {/* Apply/Regenerate buttons when audio exists and speed changed */}
        {currentAudio && hasSpeedChanged && (
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={handleApplySettings}
              className="btn btn-secondary flex-1 py-2 text-sm"
              disabled={isConverting}
            >
              ✓ Apply Speed
            </button>
            <button
              type="button"
              onClick={handleRegenerate}
              className="btn btn-primary flex-1 py-2 text-sm"
              disabled={isConverting || !text.trim()}
            >
              🔄 Regenerate
            </button>
          </div>
        )}
      </div>

      <button
        type="submit"
        className="btn btn-primary w-full"
        disabled={!text.trim() || isConverting || !selectedVoice}
      >
        {isConverting ? (
          <span className="flex items-center justify-center">
            <span className="spinner w-5 h-5 mr-2"></span>
            Generating...
          </span>
        ) : (
          <>
            <span className="mr-2">🔊</span>
            Convert to Speech
          </>
        )}
      </button>
    </form>
  );
}

export default TextInput;
